import { formatDate, formatMoney, t } from "@hakili/i18n";
import { Button, Modal } from "@hakili/ui";
import { type ReactElement, useState } from "react";

import type { DoorResult } from "../session/useSession.js";
import type { DealerSettlement } from "../session/settlements.js";

/**
 * `DEALER V2 B-D1`, ARGENT (frames `b3bafc2f`, the architect's orders of 1 November): **« Vos ventes en cours », one card per sale**, from `GET /api/v1/me/settlements` alone: the car and the buyer, the payer, « Votre part », the
 * state in the DOOR's own sentence, and the two gestures the car page already makes (`confirm-receipt` with the day of receipt, `report-non-receipt`). Nothing is drawn that no door serves: no expected date, no five-step
 * path (it waits for lane E's dossiers reader, whose `parcours` serves it), no total « à recevoir ». A settlement that is complete is no longer a sale in progress and has no card here (its car's page keeps it).
 */
const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

/** The sale is in progress while the settlement is not complete; the one that waits for the DEALER is the one he has not confirmed and has not disputed. */
export const inProgress = (settlements: readonly DealerSettlement[]): readonly DealerSettlement[] =>
  settlements.filter((one) => one.status !== "complet");
const waitsForDealer = (one: DealerSettlement): boolean =>
  one.dealerLegConfirmedAt === null && one.dealerNonReceiptReportedAt === null;

function SaleCard(props: {
  readonly settlement: DealerSettlement;
  readonly confirmReceipt: (settlementId: string, receivedOn: string) => Promise<DoorResult<unknown>>;
  readonly reportNonReceipt: (settlementId: string) => Promise<DoorResult<unknown>>;
  readonly onChanged: () => void;
  /** The frame's hierarchy: ONE primary on the page, on the first sale that waits for the dealer; the others' confirmation is a secondary. */
  readonly primary: boolean;
  /** The SERVER's day (`asOf` of the inventory door, A50), read when the dialog opens; `null` when the door says nothing. */
  readonly readToday: () => Promise<string | null>;
}): ReactElement {
  const { settlement } = props;
  const [asking, setAsking] = useState(false);
  const [receivedOn, setReceivedOn] = useState("");
  /** The server's day: the date's default and its ceiling (never in the future). `null` while unknown: no default, no ceiling. */
  const [today, setToday] = useState<string | null>(null);
  /** The refusal of the day, SAID only after an attempt (the designer's ruling of 2 November), never before the dealer acts. */
  const [dayRefusal, setDayRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const car = settlement.vehicleLabel ?? settlement.reference;
  const title = settlement.clientName === null ? car : `${car} · ${settlement.clientName}`;
  const act = async (door: () => Promise<DoorResult<unknown>>): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    const answer = await door();
    setBusy(false);
    if (!answer.ok) {
      setRefusal(answer.message);
      return;
    }
    setAsking(false);
    props.onChanged();
  };
  /** The attempt: the day is checked HERE, never before; a refusal is said under the field and nothing is sent. */
  const confirm = (): void => {
    if (receivedOn === "") {
      setDayRefusal(t("dealer.reglement.confirmerMotif"));
      return;
    }
    if (today !== null && receivedOn > today) {
      setDayRefusal(t("dealer.argent.dateFuture"));
      return;
    }
    setDayRefusal(null);
    void act(() => props.confirmReceipt(settlement.id, receivedOn));
  };
  const dayWords = /^\d{4}-\d{2}-\d{2}$/u.test(receivedOn)
    ? formatDate(new Date(`${receivedOn}T12:00:00Z`))
    : null;
  const waiting = waitsForDealer(settlement);
  return (
    <section
      data-testid={`argent-vente-${settlement.id}`}
      className={`rounded-2xl border bg-white p-5 ${waiting ? "border-amber-200 bg-amber-50/40" : "border-gray-200"}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {settlement.payerKind === "acheteur"
              ? t("dealer.argent.payeurAcheteur")
              : t("dealer.argent.payeurBanque")}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">{t("dealer.reglement.votrePart")}</p>
          <p className="text-2xl font-semibold tabular-nums text-emerald-800">
            {formatMoney(settlement.dealerAmount)}
          </p>
        </div>
      </div>
      <p className="mt-3 rounded-xl bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900">
        {settlement.blockedReason ?? t("dealer.reglement.enAttente")}
      </p>
      {settlement.dealerNonReceiptReportedAt === null ? null : (
        <p className="mt-2 text-xs text-amber-700" data-testid={`argent-rien-recu-declare-${settlement.id}`}>
          {t("dealer.reglement.rienRecuDeclare").replace(
            "{date}",
            new Date(settlement.dealerNonReceiptReportedAt).toLocaleDateString("fr-FR"),
          )}
        </p>
      )}
      {waiting ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant={props.primary ? "primary" : "secondary"}
            {...(props.primary ? { "data-awaits": "dealer" } : {})}
            onClick={() => {
              void (async () => {
                const day = await props.readToday();
                setToday(day);
                setReceivedOn(day ?? "");
                setDayRefusal(null);
                setAsking(true);
              })();
            }}
            data-testid={`argent-confirmer-${settlement.id}`}
          >
            {t("dealer.argent.confirmer")}
          </Button>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              void act(() => props.reportNonReceipt(settlement.id));
            }}
            data-testid={`argent-rien-recu-${settlement.id}`}
          >
            {t("dealer.reglement.rienRecu")}
          </Button>
        </div>
      ) : null}
      {refusal === null ? null : (
        <p role="alert" className="mt-2 text-xs text-rose-600" data-testid={`argent-refus-${settlement.id}`}>
          {refusal}
        </p>
      )}
      <Modal
        open={asking}
        size="lg"
        title={fill("dealer.argent.question", { voiture: car })}
        onClose={() => {
          setAsking(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setAsking(false);
              }}
              data-testid="argent-annuler"
            >
              {t("common.action.annuler")}
            </Button>
            <Button variant="primary" disabled={busy} onClick={confirm} data-testid="argent-confirmer-oui">
              {t("dealer.argent.ouiConfirme")}
            </Button>
          </>
        }
      >
        <div data-testid="argent-confirmation">
          <p className="text-sm text-gray-600">
            {fill("dealer.argent.corps", { montant: formatMoney(settlement.dealerAmount) })}
          </p>
          <label className="mt-3 block text-sm font-medium text-gray-900">
            {t("dealer.reglement.dateReception")}
            <input
              type="date"
              value={receivedOn}
              max={today ?? undefined}
              onChange={(event) => {
                setReceivedOn(event.target.value);
                setDayRefusal(null);
              }}
              data-testid="argent-date"
              className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
          {dayWords === null ? null : (
            <p className="mt-1 text-sm text-gray-600" data-testid="argent-date-mots">
              {dayWords}
            </p>
          )}
          {dayRefusal === null ? null : (
            <p role="alert" className="mt-2 text-sm text-gray-700" data-testid="argent-date-erreur">
              {dayRefusal}
            </p>
          )}
        </div>
      </Modal>
    </section>
  );
}

export function Sales(props: {
  readonly settlements: readonly DealerSettlement[];
  readonly confirmReceipt: (settlementId: string, receivedOn: string) => Promise<DoorResult<unknown>>;
  readonly reportNonReceipt: (settlementId: string) => Promise<DoorResult<unknown>>;
  readonly onChanged: () => void;
  readonly readToday: () => Promise<string | null>;
}): ReactElement | null {
  const sales = inProgress(props.settlements);
  if (sales.length === 0) return null;
  const firstWaiting = sales.find(waitsForDealer)?.id ?? null;
  return (
    <section
      data-testid="argent-ventes"
      className="flex flex-col gap-3"
      aria-label={t("dealer.argent.ventes")}
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        {t("dealer.argent.ventes")}
      </h2>
      {sales.map((one) => (
        <SaleCard
          key={one.id}
          settlement={one}
          confirmReceipt={props.confirmReceipt}
          reportNonReceipt={props.reportNonReceipt}
          onChanged={props.onChanged}
          primary={one.id === firstWaiting}
          readToday={props.readToday}
        />
      ))}
    </section>
  );
}
